<template>
    <div class="card">
        <Button rounded label="Invite User" icon="pi pi-plus" class="mb-1" @click="inviteUser" data-cy="invite-user" />
        <Dialog header="Invite User" v-model:visible="showInviteUserDialog" modal style="width: 400px">
            <CustomForm :fields="inviteUserFields" v-model="newUser" class="w-full" />
            <template #footer>
                <div class="flex justify-start items-center gap-1   ">
                    <LoadingState v-if="inviteLoading" small />
                    <Button rounded icon="pi pi-send" label="Invite" @click="saveUser" :disabled="!newUser.email || inviteLoading" data-cy="invite-confirm" />
                </div>
            </template>
        </Dialog>
        <Dialog v-model:visible="showOwnershipDialog" header="Transfer ownership">
            <div class="mb-1">
                This operation will transfer the ownership all folders and graphs to another user
            </div>
            <div class="flex gap-1 items-center">
                <Select v-model="newOwner" :options="users.filter(u => u.id != selectedActionUserId)" optionLabel="email" placeholder="Select a user" :disabled="transferingOwnership" />
                <Button rounded icon="pi pi-arrow-right-arrow-left" label="Transfer" @click="transferOwnership" :disabled="transferingOwnership || !newOwner" data-cy="transfer-confirm"/>
                <LoadingState v-if="transferingOwnership" small />
            </div>
        </Dialog>
        <Dialog v-model:visible="showDeleteDialog" header="Delete User">
            <div class="mb-1">
                Please select a user to transfer the ownership of all folders and graphs to before deleting the user
            </div>
            <div class="flex gap-1 items-center">
                <Select v-model="newOwner" :options="users.filter(u => u.id != selectedActionUserId)" optionLabel="email" placeholder="Select a user" :disabled="transferingOwnership" />
                <Button rounded icon="pi pi-times-circle" class="ml-1" label="Transfer and delete" @click="transferAndDelete" :disabled="transferingOwnership || !newOwner" data-cy="transfer-delete-confirm"/>
                <LoadingState v-if="transferingOwnership" small />
            </div>
        </Dialog>
        <Dialog v-model:visible="showResetPasswordDialog" header="Are you sure you want to reset the password of this user?">
            <div class="mb-1">
                An email with a reset link will be sent to the user's email address
            </div>
            <template #footer>
                <div class="flex gap-1 items-center">
                    <LoadingState v-if="resettingPassword" small />
                    <Button rounded icon="pi pi-key" label="Reset" @click="resetPassword" :disabled="resettingPassword" data-cy="reset-confirm"/>
                </div>
            </template>
        </Dialog>
        <DataTable sortField="email" :sortOrder="1" v-model:filters="filters" :value="users" paginator showGridlines :rows="10" dataKey="id"
                   removableSort scrollable scrollHeight="calc(100vh - 350px)"
                filterDisplay="menu" :loading="loading" :globalFilterFields="['email', 'group_string', 'first_login', 'last_login', 'status']">
            <template #header>
                <div class="flex justify-between">
                    <Button rounded type="button" icon="pi pi-filter-slash" label="Clear" outlined @click="clearFilter()" />
                    <IconField iconPosition="left">
                        <InputIcon>
                            <i class="pi pi-search" />
                        </InputIcon>
                        <InputText v-model="filters['global'].value" placeholder="Keyword Search" />
                    </IconField>
                </div>
            </template>
            <template #empty> No users found. </template>
            <template #loading> Loading users data. Please wait. </template>
            <Column sortable field="email" header="Email" style="min-width: 12rem">
                <template #body="{ data }">
                    {{ data.email }} {{ data.last_login ? '' : '(Invited)' }}
                </template>
                <template #filter="{ filterModel }">
                    <InputText v-model="filterModel.value" type="text" class="p-column-filter" placeholder="Search by email" />
                </template>
            </Column>
            <Column sortable header="Status" field="status" :filterMenuStyle="{ width: '14rem' }" style="min-width: 12rem">
                <template #body="{ data }">
                    <div class="flex gap-1" v-if="selectedActionUserId != data.id || editType != 'status'">
                        <i class="pi pi-pencil cursor-pointer" @click="editStatus(data)" data-cy="edit-status"></i>
                        <Tag :value="data.status" :severity="getSeverity(data.status)" />
                    </div>
                    <div v-else>
                        <InputGroup v-if="!updatingUser">
                            <SelectButton v-model="statusToEdit" :options="['active', 'inactive']" :allowEmpty="false"></SelectButton>
                            <Button icon="pi pi-check" severity="success" @click="updateStatus"/>
                            <Button icon="pi pi-times" severity="danger" @click="selectedActionUserId = null" />
                        </InputGroup>
                        <LoadingState v-else small ></LoadingState>

                    </div>
                </template>
                <template #filter="{ filterModel }">
                    <Select v-model="filterModel.value" :options="statuses" placeholder="Select One" class="p-column-filter" showClear>
                        <template #option="slotProps">
                            <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
                        </template>
                    </Select>
                </template>
            </Column>
            <Column sortable header="Roles" field="roles" :filterMenuStyle="{ width: '14rem' }" style="min-width: 12rem">
                <template #body="{ data }">
                    <div class="flex gap-1" v-if="selectedActionUserId != data.id || editType != 'role'">
                        <i class="pi pi-pencil cursor-pointer" @click="editRole(data)" data-cy="edit-role"></i>
                        <Tag v-for="role in data.roles" :value="_.upperFirst(_.camelCase(role))" />
                    </div>
                    <div v-else>
                        <InputGroup v-if="!updatingUser">
                            <MultiSelect v-model="rolesToEdit" display="chip" :options="roles" optionLabel="name" optionValue="id" placeholder="Select Roles"
                            :maxSelectedLabels="3" class="w-full md:w-80" />
                            <Button icon="pi pi-check" severity="success" @click="updateRoles"/>
                            <Button icon="pi pi-times" severity="danger" @click="selectedActionUserId = null" />
                        </InputGroup>
                        <LoadingState v-else small ></LoadingState>
                    </div>
                </template>
                <template #filter="{ filterModel }">
                    <MultiSelect v-model="filterModel.value" :options="roles" optionLabel="name" placeholder="Any" class="p-column-filter">
                        <template #option="slotProps">
                            <div class="flex items-center gap-2">
                                <span>{{ slotProps.option.name }}</span>
                            </div>
                        </template>
                    </MultiSelect>
                </template>
            </Column>
            <Column header="Groups" field="groups" :filterMenuStyle="{ width: '14rem' }" style="min-width: 12rem">
                <template #body="{ data }">
                    <div class="flex gap-1" v-if="selectedActionUserId != data.id || editType != 'group'">
                        <i class="pi pi-pencil cursor-pointer" @click="editGroup(data)" data-cy="edit-group"></i>
                        <Tag v-for="group in data.groups" :value="group.name" />
                    </div>
                    <div v-else>
                        <InputGroup v-if="!updatingUser">
                            <MultiSelect v-model="groupsToEdit" display="chip" :options="groups" optionValue="id" optionLabel="name" placeholder="Select Groups"
                            :maxSelectedLabels="3" class="w-full md:w-80" />
                            <Button icon="pi pi-check" severity="success" @click="updateGroups"/>
                            <Button icon="pi pi-times" severity="danger" @click="selectedActionUserId = null" />
                        </InputGroup>
                        <LoadingState v-else small ></LoadingState>
                    </div>
                </template>
                <template #filter="{ filterModel }">
                    <MultiSelect v-model="filterModel.value" :options="groups" optionLabel="name" placeholder="Any" class="p-column-filter">
                        <template #option="slotProps">
                            <div class="flex items-center gap-2">
                                <span>{{ slotProps.option.name }}</span>
                            </div>
                        </template>
                    </MultiSelect>
                </template>
            </Column>
            <Column header="Actions">
                <template #body="{ data }">
                    <div class="flex gap-2">
                        <Button v-if="data.last_login" icon="pi pi-arrow-right-arrow-left" text raised rounded severity="success" v-tooltip="'Transfer Ownership'" @click="showTransfer(data)" />
                        <Button icon="pi pi-times-circle" text raised rounded severity="danger" v-tooltip="'Delete User'" @click="showDelete(data)" />
                        <Button v-if="data.provider_id == 'password'" icon="pi pi-key" text raised rounded severity="danger" v-tooltip="'Reset Password'" @click="showResetPassword(data)" />
                    </div>
                </template>
            </Column>
            <Column sortable header="Creation date" filterField="first_login" field="first_login" dataType="date">
                <template #body="{ data }">
                    {{ data.first_login_formatted }}
                </template>
                <template #filter="{ filterModel }">
                    <DatePicker v-model="filterModel.value" :dateFormat="primeVueDateFormat" :placeholder="primeVueDateFormat"/>
                </template>
            </Column>
            <Column sortable header="Last login" filterField="last_login" field="last_login" dataType="date">
                <template #body="{ data }">
                    {{ data.last_login_formatted }}
                </template>
                <template #filter="{ filterModel }">
                    <DatePicker v-model="filterModel.value" :dateFormat="primeVueDateFormat" :placeholder="primeVueDateFormat" />
                </template>
            </Column>


        </DataTable>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import _ from 'lodash-es'
import {parseDateTime, primeVueDateFormat} from "@/src/utils/utils";

import {useToast} from 'primevue/usetoast'
const toast = useToast()

import { useStore } from 'vuex';
import {OP, trackPending, usePending} from "@/src/queries/pending";
import {useLastApiError} from "@/src/queries/errors";
import {resetUserPassword, transferOwnership as transferOwnershipApi} from "@/src/api/users";
const store = useStore();

const ssoEnabled = import.meta.env.VITE_FIREBASE_SSO_ENABLED === 'true'
const basicAuthEnable = import.meta.env.VITE_BASIC_AUTHENTICATION_ENABLED === 'true'

const lastError = useLastApiError()

const emailExistError = computed(() => lastError.value?.code === 'FIREBASE_EMAIL_ALREADY_EXISTS')

// `POST users/invite/` reports validation under `error.fields`; ApiError normalises that
// and `error.field_errors` into the same `fieldErrors` map.
const inviteUserEmailError = computed(() => lastError.value?.fieldErrors?.email?.[0] ?? null)

const inviteUserFields = computed(() => {
    return [
        {
            elementName: 'InputText',
            dataField: 'email',
            label: 'Email',
            class: 'w-full',
            invalidValue: inviteUserEmailError.value || emailExistError.value,
            invalidValueMessage: inviteUserEmailError.value || (emailExistError.value ? 'Email already exists' : null),
            visible: true,
        },
        {
            elementName: 'SelectButton',
            dataField: 'type',
            label: 'Login Type',
            options: [
                { label: 'SSO', value: 'sso' },
                { label: 'Email/PW', value: 'basic' }
            ],
            optionLabel: 'label',
            optionValue: 'value',
            allowEmpty: false,
            visible: ssoEnabled && basicAuthEnable
        }
    ].filter(f => f.visible)
})

const filters = ref();
const statuses = ref(['active', 'inactive']);
const loading = ref(true);

const groups = computed(() => store.getters['userStore/groups']);
const roles = [
    { name: 'GraphAdmin', id: 'graph_admin'},
    { name: 'ChatbotAdmin', id: 'chatbot_admin' },
    { name: 'UserAdmin', id: 'user_admin'},
    { name: 'SystemAdmin', id: 'system_admin'},
    { name: 'Admin', id: 'admin' },
];

onMounted(async () => {
    loading.value = false
});


const initFilters = () => {
    filters.value = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        email: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
        groups: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.IN }] },
        last_login: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        first_login: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
        roles: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    };
};

initFilters();

const clearFilter = () => {
    initFilters();
};

const getSeverity = (status) => {
    switch (status) {
        case 'inactive':
            return 'danger';

        case 'active':
            return 'success';

    }
};

const selectedActionUserId = ref(null)
const editType = ref(null)

const rolesToEdit = ref(null)
const editRole = (data) => {
    editType.value = 'role'
    selectedActionUserId.value = data.id
    rolesToEdit.value = data.roles
}

const updateRoles = async () => {
    await store.dispatch('userStore/editUser', {
        id: selectedActionUserId.value,
        data: {
            roles: rolesToEdit.value
        }
    })
    selectedActionUserId.value = null
}

const groupsToEdit  = ref([])
const editGroup = (data) => {
    editType.value = 'group'
    selectedActionUserId.value = data.id
    groupsToEdit.value = data.groups.map(g => g.id)
}

const users = computed(() => {
    return store.getters['userStore/users'].map(u => {
        // Keep original date objects for filtering, store formatted strings separately
        u.first_login_formatted = parseDateTime(u.first_login)
        u.last_login_formatted = parseDateTime(u.last_login)
        // Convert to Date objects for filtering if they are strings
        if (u.first_login && typeof u.first_login === 'string') {
            u.first_login = new Date(u.first_login)
        }
        if (u.last_login && typeof u.last_login === 'string') {
            u.last_login = new Date(u.last_login)
        }
        u.group_string = u.groups.map(g => g.name).join(', ')
        return u
    })
})

const updateGroups = async () => {
    await store.dispatch('userStore/editUser', {
        id: selectedActionUserId.value,
        data: {
            groups: groupsToEdit.value
        }
    })
    selectedActionUserId.value = null
}

const statusToEdit = ref(null)
const editStatus = (data) => {
    editType.value = 'status'
    selectedActionUserId.value = data.id
    statusToEdit.value = data.status
}

const updateStatus = async () => {
    await store.dispatch('userStore/editUser', {
        id: selectedActionUserId.value,
        data: {
            status: statusToEdit.value
        }
    })
    selectedActionUserId.value = null
}

const updatingUser = usePending([OP.EDIT_USER, OP.USERS])

const showOwnershipDialog = ref(false)
const newOwner = ref(null)

const showTransfer = (data) => {
    selectedActionUserId.value = data.id
    showOwnershipDialog.value = true
}

const showDeleteDialog = ref(false)
const showDelete = async (data) => {
    selectedActionUserId.value = data.id
    showDeleteDialog.value = true
}

const transferOwnership = async () => {
    await trackPending(OP.TRANSFER_OWNERSHIP, () =>
        transferOwnershipApi(selectedActionUserId.value, newOwner.value.id))
    showOwnershipDialog.value = false
}

const transferAndDelete = async () => {
    await trackPending(OP.TRANSFER_OWNERSHIP, () =>
        transferOwnershipApi(selectedActionUserId.value, newOwner.value.id))
    await store.dispatch('userStore/deleteUser', selectedActionUserId.value)
    showDeleteDialog.value = false
}

const transferingOwnership = usePending([OP.TRANSFER_OWNERSHIP, OP.DELETE_USER])

const showInviteUserDialog = ref(false)
const newUser = ref({
    email: '',
    type: ssoEnabled ? 'sso' : 'basic'
})

const inviteUser = () => {
    newUser.value = {
        email: '',
        type: ssoEnabled ? 'sso' : 'basic'
    }
    showInviteUserDialog.value = true
}

const saveUser = async () => {
    await store.dispatch('userStore/inviteUser', newUser.value)
    showInviteUserDialog.value = false
    toast.add({severity:'success',
        summary: 'User Invited',
        detail: `User invited successfully`, life: 5000});
}

const inviteLoading = usePending(OP.INVITE_USER)

const showResetPasswordDialog = ref(false)
const showResetPassword = (data) => {
    selectedActionUserId.value = data.id
    showResetPasswordDialog.value = true
}

const resetPassword = async () => {
    await trackPending(OP.RESET_PASSWORD, () => resetUserPassword(selectedActionUserId.value))
    showResetPasswordDialog.value = false
    toast.add({severity:'success',
        summary: 'Password Reset',
        detail: `Reset link sent via email`, life: 5000});
}
const resettingPassword = usePending(OP.RESET_PASSWORD)
</script>
